import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs';
import { SharedCommonService } from '../services/shared-common.service';
import { LoadingService } from '../services/loading.service';

let requestCount = 0;

/**
 * An HTTP interceptor function that tracks ongoing HTTP requests and manages the visibility
 * of a loading indicator based on the request activity.
 *
 * This interceptor uses the `SharedCommonService` to show or hide a loader when HTTP requests
 * are being processed. It increments a request counter for all requests and ensures the
 * loader is displayed while requests are active. Once all requests are completed, the
 * loader is hidden. It respects custom loading states managed by LoadingService.
 *
 * @param req - The outgoing HTTP request.
 * @param next - The next handler in the HTTP request pipeline.
 * @returns An observable of the HTTP event stream, with the loader visibility managed during
 *          the lifecycle of the request.
 *
 * Functionality:
 * - Injects `SharedCommonService` to control the loader visibility.
 * - Tracks requests using a counter (`requestCount`) to ensure the loader is only hidden
 *   when all requests are completed.
 * - Ensures the request counter does not drop below zero.
 * - Respects custom loading states from LoadingService to avoid conflicts.
 * - Uses the `finalize` operator to handle cleanup logic after the request completes.
 */
export const loaderInterceptor: HttpInterceptorFn = (req, next) => {
  const sharedService = inject(SharedCommonService);
  const loadingService = inject(LoadingService);
  
  requestCount++;
  
  // Only show default loader if no custom loader with message is active
  if (!loadingService.isCustomLoaderActive()) {
    sharedService.showLoader();
  }
  
  return next(req).pipe(
    finalize(() => {
      requestCount = Math.max(0, requestCount - 1); // Prevent negative values
      
      // Only hide loader if no requests are pending and no custom loader is active
      if (requestCount === 0 && !loadingService.isCustomLoaderActive()) {
        sharedService.hideLoader();
      }
    })
  );
};